<script lang="ts" module>
  import {
    type ActionHandler,
    type DialogAction,
    type DialogForm,
    type PromptDict,
    type PromptLines,
  } from './modals.ts';

  export type PromptText =
    | string
    | {
        intro?: PromptLines | undefined;
        action?: PromptLines | undefined;
        outro?: PromptLines | undefined;
      };

  export interface PromptModalProps<T extends string, F extends FormType, CS extends string>
    extends MakeOptional<ModalProps<DialogAction<T>>> {
    color?: string | undefined;
    icon?: string | undefined;
    header?: string | undefined;
    text?: PromptText | undefined;
    dict?: PromptDict | undefined;
    position?: 'top' | 'center' | 'bottom' | undefined;
    styleClass?: string | undefined;

    form?: DialogForm<F> | undefined;
    formError?: string | undefined;

    // TODO: Create a metadata interface for each Action instead of piecemeal.
    actions?: Array<DialogAction<T>> | undefined;
    labels?: Partial<MapRec<DialogAction<T>, string>> | undefined;
    schemes?: Partial<MapRec<DialogAction<T>, CS>> | undefined;
    colorMap?: Record<CS, ColorScheme<string>> | undefined;
    // Custom actions can handle actions, manipulate the form, and cancel the dialog from closing.
    actionHandlers?: Partial<MapRec<T, ActionHandler<T>>> | undefined;
    // The callback handles the action which closed the dialog.
    cb?: (action: DialogAction<T>) => void | undefined;

    children?: Snippet | undefined;
    // [key: string]: unknown;
    debug?: boolean | undefined;
  }
</script>

<script lang="ts" generics="T extends string, F extends FormType, CS extends string = never">
  import { enhance } from '$app/forms';
  import { ignore } from '@yp/common/async.ts';
  import { arrayOf } from '@yp/common/containers.ts';
  import { assertPresent, assertValid, log } from '@yp/common/core.ts';
  import {
    isAbsent,
    isBoolean,
    isPresent,
    isString,
    recordFromEntries,
    type MakeOptional,
    type MapRec,
  } from '@yp/common/identity.ts';
  import { coerce } from '@yp/common/schema/validate.ts';
  import { propToLabel } from '@yp/common/strings.ts';
  import type { SubmitFunction } from '@sveltejs/kit';
  import { onMount, type Snippet } from 'svelte';
  import { createDialog } from 'svelte-headlessui';
  import { onBeforeClose, type ModalProps } from 'svelte-modals';
  import Transition from 'svelte-transition';
  import { AuthenticationError } from '../client/errors.ts';
  import { CommonStyle, type FormType } from '../forms/fields.ts';
  import { FieldList } from '../forms/index.ts';
  import { type ColorScheme } from '../styles.ts';
  import { Button, Icon } from './index.ts';
  import { CancelAction, ModalFont } from './modals.ts';
  import { PromptLinesDom } from './prompt_lines_dom.ts';

  let {
    // Modals sets this to "is on top of the modal stack"
    isOpen = false,
    // The following props aren't set when the modal is a static DOM element.
    id = 'n/a',
    index = -1,
    close: closeModal = () => true,
    color = undefined,
    icon = undefined,
    header = undefined,
    text = undefined,
    dict = undefined,
    position = 'center',
    styleClass = undefined,
    form = $bindable(),
    formError = undefined,
    actions = undefined,
    labels = undefined,
    schemes = undefined,
    colorMap = undefined,
    actionHandlers = undefined,
    cb = undefined,
    children = undefined,
    debug = false,
  }: PromptModalProps<T, F, CS> = $props();

  const activeColor = color ?? 'blue';

  const primaryAction = actions?.at(0);
  let exitAction: DialogAction<T> = CancelAction;

  let fieldList = $state<FieldList<F>>();
  let disabled = $state(false);
  let focusWhenEnabled = false;

  const dialog = createDialog({ label: header ?? '' });

  const valid = $derived(form ? isPresent(coerce(form.value, form.desc).valid) : true);

  let expectClose = false;

  export function focus(focused = true) {
    if (isOpen) {
      if (!disabled) {
        fieldList?.focus(focused);
      } else if (focused) {
        focusWhenEnabled = true;
      }
    }
  }

  $effect(() => {
    if (!disabled && focusWhenEnabled) {
      focusWhenEnabled = false;
      fieldList?.focus();
    }
  });

  export function doOpen() {
    expectClose = false;
    dialog.open();
    form = form;
  }

  export function doDismiss(action: DialogAction<T> = 'cancel') {
    expectClose = true;
    exitAction = action;
    // Close the headlessui visual modal. This works for static and dynamic modals.
    dialog.close();
    // Close the svelte-modals modal. This works for dynamic modals only.
    closeModal(action);
  }

  export function onBackdropClicked(e: Event) {
    // This is super awkward since both modal systems are trying to handle this.
    e.preventDefault();
    doDismiss();
  }

  function onAction(action: DialogAction<T>) {
    if (debug) {
      console.log('PromptModal.onAction', action);
    }
    const handled = action === 'cancel' || (actionHandlers?.[action]?.(action) ?? true);
    if (isBoolean(handled)) {
      disabled = false;
      if (!handled) {
        return;
      }
      doDismiss(action);
    } else {
      disabled = true;
      ignore(
        handled
          .then((handled) => {
            if (handled !== false) {
              doDismiss(action);
            }
          })
          .finally(() => {
            disabled = false;
          }),
      );
    }
  }

  function onVerify(): boolean {
    assertPresent(form);

    function explain(reason?: string) {
      if (isAbsent(reason)) {
        return undefined;
      }
      if (reason === 'required') {
        return '';
      }
      // not a/an
      if (reason.startsWith('not a')) {
        return '';
      }
      return '';
    }

    const result = coerce(form.value, form.desc);
    if (!result.valid) {
      if (debug) {
        log('PromptModal.onVerify', { errors: result.errors });
      }
      form.errors = recordFromEntries(
        result.errors
          .map((e) => [e.field, explain(e.reason)] as const)
          .filter(([f, r]) => form!.desc.some((d) => d.k === f) && isPresent(r)),
      ) as Partial<MapRec<keyof F, string>>;
      // formError = 'There are some problems to address.'
      return false;
    }

    fieldList?.focus(false);
    formError = undefined;
    delete form.errors;
    disabled = true;
    form = form;
    return true;
  }

  function onFakeSubmit() {
    if (debug) {
      console.log('PromptModal.onFakeSubmit', { form });
    }
    assertValid(form?.action, isAbsent);

    if (isAbsent(form) || onVerify()) {
      onAction(primaryAction ?? CancelAction);
    }
  }

  // A typed version of SubmitFunction is in the ./+types.js file of the current page.
  const onSubmit: SubmitFunction = ({ cancel }) => {
    if (debug) {
      console.log('PromptModal.onSubmit', { form });
    }
    assertPresent(form?.action);

    if (!onVerify()) {
      cancel();
      return;
    }

    if (debug) {
      console.log('PromptModal.onSubmit start', { disabled });
    }

    return async ({ result }) => {
      disabled = false;
      if (debug) {
        console.log('PromptModal.onSubmit end', { result, disabled });
      }
      if (result.type === 'error') {
        if (result.status === 401) {
          throw new AuthenticationError();
        } else {
          formError = `${result.error.message} (Code: ${result.status})`;
        }
      } else if (result.type === 'failure') {
        formError = String(result.data?.error);
      } else if (result.type === 'success') {
        onAction(primaryAction ?? CancelAction);
      }
    };
  };

  // TODO: Consider using the button version of this (formaction).
  // https://kit.svelte.dev/docs/form-actions#named-actions
  const formAction = isPresent(form?.action)
    ? `${form.actionUrl ?? ''}?/${form.action ?? ''}`
    : undefined;

  const actionButtons = $state(
    arrayOf<HTMLButtonElement | undefined>((actions?.length ?? 0) + 1, undefined),
  );

  function afterEnter() {
    // Note: 'autofocus' attribute only works reliably on page load, which this probably isn't.

    const wasFocused = fieldList?.focus() ?? false;
    if (!wasFocused) {
      actionButtons[actionButtons.length - 1]?.focus({ preventScroll: true });
    }
  }

  if (debug) {
    $effect(() => log('PromptModal', { id, index, isOpen, disabled, expanded: $dialog.expanded }));
  }

  if (isOpen) {
    onBeforeClose(() => {
      if (debug) {
        console.log('PromptModal.onBeforeClose', { id, index, disabled, exitAction, expectClose });
      }
      if (disabled) {
        return false;
      }
      if (!expectClose) {
        // Make sure we're visibly closed in case of an extrinsic svelte-modals close.
        dialog.close();
      }
      expectClose = false;
      cb?.(exitAction);
      return true;
    });

    onMount(doOpen);
  } else {
    isOpen = true;
  }
</script>

<Transition show={($dialog.expanded && isOpen) || disabled}>
  <div class="relative z-10">
    <Transition
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
      on:after-enter={afterEnter}>
      <div class="fixed inset-0 bg-black/25 transition-opacity"></div>
    </Transition>

    <div class="fixed inset-0 z-10 w-screen overflow-y-auto" onclose={(e) => onBackdropClicked(e)}>
      <div
        class="flex min-h-full {position === 'top'
          ? 'items-start'
          : position === 'center'
            ? 'items-center'
            : 'items-end'}
          justify-center p-4 text-center sm:items-center sm:p-0">
        <Transition
          enter="ease-out duration-300"
          enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          enterTo="opacity-100 translate-y-0 sm:scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 translate-y-0 sm:scale-100"
          leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
          <div
            use:dialog.modal
            class="{styleClass ??
              ''} {ModalFont} relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-md sm:max-w-lg sm:my-8 px-4 pb-4 pt-5 sm:p-6">
            <div class="sm:flex sm:items-start">
              <div class="text-center sm:ml-4 sm:mt-0 sm:text-left">
                <div class="flex flex-nowrap">
                  {#if icon}
                    <div class="mr-2">
                      <Icon {icon} style={`color: ${activeColor}`} />
                    </div>
                  {/if}
                  {#if header}
                    <div class="text-base font-semibold leading-6 text-gray-900">{header}</div>
                  {/if}
                </div>
                {#if isString(text) || text?.intro}
                  <div class="mt-2">
                    <PromptLinesDom
                      {dict}
                      lines={isString(text) ? [text] : text.intro}
                      class="mt-1 text-sm text-gray-700"
                      {onAction} />
                  </div>
                {/if}
              </div>
            </div>
            <form method="POST" action={formAction} use:enhance={onSubmit}>
              {#if form}
                <div class="mt-4 sm:ml-8 sm:mt-2 sm:flex">
                  <FieldList
                    bind:this={fieldList}
                    {dict}
                    {form}
                    {disabled}
                    labelColor={activeColor}
                    on:update={() => (form = form)} />
                </div>
              {/if}
              {@render children?.()}
              {#if !isString(text) && text?.action}
                <div class="mt-2">
                  <PromptLinesDom {dict} lines={text.action} class="mt-1 text-sm text-gray-700" />
                </div>
              {/if}
              {#if formError}
                <p class="mt-2 text-sm text-red-600" id="form-error">{formError}</p>
              {/if}
              <div
                class="mt-4 sm:mt-8 flex gap-4 sm:ml-4 {(actions?.length ?? 0) < 2
                  ? 'flex-row'
                  : 'flex-col'}">
                {#each [...(actions ?? []), CancelAction] as action, i (action)}
                  {@const primary = action === primaryAction}
                  {@const cancel = action === CancelAction}
                  {@const scheme = schemes?.[action]}
                  <Button
                    name={action}
                    role={primary ? 'submit' : 'button'}
                    color={isAbsent(scheme) && primary ? activeColor : undefined}
                    scheme={isPresent(scheme) ? colorMap?.[scheme] : undefined}
                    class={`sm:min-w-32 ${CommonStyle.transition}`}
                    onclick={primary
                      ? isPresent(formAction)
                        ? undefined
                        : onFakeSubmit
                      : () => onAction(action)}
                    bind:el={actionButtons[i]}
                    disabled={disabled && !cancel}
                    fakeDisabled={primary && !valid}>
                    <!-- use:focus={action} -->
                    {labels?.[action] ?? propToLabel(action)}
                  </Button>
                {/each}
              </div>
              {#if !isString(text) && text?.outro}
                <div class="mt-3">
                  <PromptLinesDom {dict} lines={text.outro} class="mt-1 text-sm text-gray-700" />
                </div>
              {/if}
            </form>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</Transition>
